import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useHistory } from "react-router-dom";
import "./MainQuizStartComponent.css";
import { useDispatch, useSelector } from "react-redux";
import { submitBadgeQuizAction } from "../../../actions/Badge/badgeActions";

const MainQuizStartComponent = () => {
  const dispatch = useDispatch();
  const { quizDetails } = useSelector((state) => state.quizQuestionStore);
  const { result, success, loading } = useSelector(
    (state) => state.quizSubmissionStore
  );
  const history = useHistory();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(
    quizDetails?.quiz?.totalTimeSeconds || 600
  );
  const [isTimerCritical, setIsTimerCritical] = useState(false);
  const [showMotivation, setShowMotivation] = useState(false);
  const [motivationType, setMotivationType] = useState("");
  const [hasShownMilestone, setHasShownMilestone] = useState(false);
  const [quizState, setQuizState] = useState("active");

  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentLoadingIndex, setCurrentLoadingIndex] = useState(0);
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState(false);

  const backgroundAudioRef = useRef(null);
  const timerAudioRef = useRef(null);
  const criticalTimerAudioRef = useRef(null);
  const lastAnswerTimeRef = useRef(null);
  const questionTimesRef = useRef([]);
  const processingStartTimeRef = useRef(null);

  const motivationCarouselMessages = [
    {
      emoji: "🏆",
      text: "Earning a badge and sharing it on LinkedIn shows your tech skills to recruiters",
    },
    {
      emoji: "🚀",
      text: "You are already in the top 10% in tech by solving this quiz",
    },
    {
      emoji: "💡",
      text: "Certified skills help you stand out in job applications",
    },
    {
      emoji: "⭐",
      text: "Your dedication to learning sets you apart from the crowd",
    },
  ];

  const loadingMessages = [
    { text: "Analyzing your quiz...", progress: 25 },
    { text: "Preparing results...", progress: 50 },
    { text: "Preparing your badge...", progress: 75 },
    { text: "Redirecting to results page...", progress: 100 },
  ];

  const motivationMessages = {
    milestone: {
      title: "🔥 You're On Fire!",
      message: "Halfway there! Keep up the amazing work!",
      emoji: "🎯",
    },
    speed: {
      title: "⚡ Lightning Fast!",
      message: "Wow! You're crushing it! Amazing speed and accuracy!",
      emoji: "🚀",
    },
  };

  useEffect(() => {
    lastAnswerTimeRef.current = Date.now();
  }, []);

  // Handle minimum 5 second delay when processing starts
  useEffect(() => {
    if (quizState === "processing") {
      processingStartTimeRef.current = Date.now();

      const minimumDelayTimer = setTimeout(() => {
        setMinimumTimeElapsed(true);
      }, 5000);

      return () => clearTimeout(minimumDelayTimer);
    }
  }, [quizState]);

  // Handle redirect after both conditions are met
  useEffect(() => {
    if (
      quizState === "processing" &&
      minimumTimeElapsed &&
      !loading &&
      success
    ) {
      const elapsedTime = Date.now() - processingStartTimeRef.current;
      const remainingDelay = Math.max(0, 5000 - elapsedTime);

      setTimeout(() => {
        history.push(
          `/certificates/quiz-result/${result?.result?.submissionId}`
        );
      }, remainingDelay);
    }
  }, [quizState, minimumTimeElapsed, loading, success, result, history]);

  useEffect(() => {
    if (quizState === "processing") {
      const messageInterval = setInterval(() => {
        setCurrentMessageIndex(
          (prev) => (prev + 1) % motivationCarouselMessages.length
        );
      }, 3000);

      const progressInterval = setInterval(() => {
        setProcessingProgress((prev) => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + 1;
        });
      }, 50);

      const loadingInterval = setInterval(() => {
        setCurrentLoadingIndex((prev) => {
          const nextIndex = prev + 1;
          if (nextIndex >= loadingMessages.length) {
            clearInterval(loadingInterval);
            return prev;
          }
          return nextIndex;
        });
      }, 1250);

      return () => {
        clearInterval(messageInterval);
        clearInterval(progressInterval);
        clearInterval(loadingInterval);
      };
    }
  }, [quizState]);

  const checkSpeedMilestone = () => {
    const uniqueAnswered = [];
    const seen = new Set();

    for (let i = questionTimesRef.current.length - 1; i >= 0; i--) {
      const entry = questionTimesRef.current[i];
      if (!seen.has(entry.id)) {
        uniqueAnswered.unshift(entry);
        seen.add(entry.id);
      }
      if (uniqueAnswered.length === 4) break;
    }

    if (uniqueAnswered.length >= 3) {
      const timeSpan =
        uniqueAnswered[uniqueAnswered.length - 1].time - uniqueAnswered[0].time;

      if (timeSpan < 90000 && !hasShownMilestone) {
        setMotivationType("speed");
        setShowMotivation(true);
        setHasShownMilestone(true);

        setTimeout(() => {
          setShowMotivation(false);
        }, 2000);
      }
    }
  };

  useEffect(() => {
    if (quizState === "active" && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setQuizState("timeout");
            return 0;
          }
          if (prev === 121) {
            setIsTimerCritical(true);
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [quizState, timeRemaining]);

  useEffect(() => {
    if (quizState === "active") {
      if (backgroundAudioRef.current) {
        backgroundAudioRef.current
          .play()
          .catch((e) => console.log("Audio play failed:", e));
      }
      if (timerAudioRef.current) {
        timerAudioRef.current
          .play()
          .catch((e) => console.log("Audio play failed:", e));
      }
    } else {
      if (backgroundAudioRef.current) backgroundAudioRef.current.pause();
      if (timerAudioRef.current) timerAudioRef.current.pause();
      if (criticalTimerAudioRef.current) criticalTimerAudioRef.current.pause();
    }
  }, [quizState]);

  useEffect(() => {
    if (isTimerCritical && quizState === "active") {
      if (timerAudioRef.current) {
        timerAudioRef.current.pause();
      }
      if (criticalTimerAudioRef.current) {
        criticalTimerAudioRef.current
          .play()
          .catch((e) => console.log("Audio play failed:", e));
      }
    }
  }, [isTimerCritical, quizState]);

  useEffect(() => {
    const answeredCount = Object.keys(answers).length;

    if (answeredCount === 5 && !hasShownMilestone) {
      setMotivationType("milestone");
      setShowMotivation(true);
      setHasShownMilestone(true);
    }
  }, [answers, hasShownMilestone]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const handleSelectOption = (data) => {
    const currentTime = Date.now();

    if (answers[data?.questionId] === undefined) {
      questionTimesRef.current.push({
        id: data?.questionId,
        time: currentTime,
      });
    }

    setAnswers((prev) => ({
      ...prev,
      [currentQuestion]: data,
    }));

    setTimeout(() => checkSpeedMilestone(), 100);
  };

  const handleQuestionNavigation = (questionIndex) => {
    if (answers[currentQuestion] !== undefined) {
      setCurrentQuestion(questionIndex);
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizDetails?.quiz?.totalQuestions - 1) {
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handleSubmit = () => {
    setQuizState("processing");
    const formattedAnswers = Object.values(answers);
    dispatch(submitBadgeQuizAction(quizDetails?.quiz?._id, formattedAnswers));
  };

  const handleCloseMotivation = () => {
    setShowMotivation(false);
  };

  const allQuestionsAnswered =
    Object.keys(answers).length === quizDetails?.quiz?.totalQuestions;
  const isLastQuestion =
    currentQuestion === quizDetails?.quiz?.totalQuestions - 1;
  const currentQuestionAnswered = answers[currentQuestion] !== undefined;

  return (
    <div className="mainQuizStart">
      <audio ref={backgroundAudioRef} loop>
        <source src="/audio/background-music.mp3" type="audio/mpeg" />
      </audio>
      <audio ref={timerAudioRef} loop>
        <source src="/audio/timer-tick.mp3" type="audio/mpeg" />
      </audio>
      <audio ref={criticalTimerAudioRef} loop>
        <source src="/audio/critical-timer.mp3" type="audio/mpeg" />
      </audio>

      <AnimatePresence>
        {showMotivation && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mainQuizStart__motivationOverlay"
            onClick={handleCloseMotivation}
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.5, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 15 }}
              className="mainQuizStart__motivationDialog"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="mainQuizStart__motivationEmoji"
              >
                {motivationMessages[motivationType]?.emoji}
              </motion.div>
              <h2 className="mainQuizStart__motivationTitle">
                {motivationMessages[motivationType]?.title}
              </h2>
              <p className="mainQuizStart__motivationMessage">
                {motivationMessages[motivationType]?.message}
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence mode="wait">
        {quizState === "active" && (
          <motion.div
            key="active"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 20 }}
            className="mainQuizStart__quizScreen"
          >
            <div className="mainQuizStart__scrollableContent">
              <motion.div
                animate={{
                  backgroundColor: isTimerCritical ? "#ff4444" : "#C13584",
                }}
                className={`mainQuizStart__timer ${
                  isTimerCritical ? "mainQuizStart__timer--critical" : ""
                }`}
              >
                <svg
                  className="mainQuizStart__timerIcon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <circle cx="12" cy="12" r="10" strokeWidth="2" />
                  <path d="M12 6v6l4 2" strokeWidth="2" />
                </svg>
                <span className="mainQuizStart__timerText">
                  {formatTime(timeRemaining)}
                </span>
              </motion.div>

              <div className="mainQuizStart__questionNumbers">
                {Array.from(
                  { length: quizDetails?.quiz?.totalQuestions },
                  (_, i) => (
                    <motion.button
                      key={i}
                      whileHover={{ scale: answers[i] !== undefined ? 1.1 : 1 }}
                      whileTap={{ scale: answers[i] !== undefined ? 0.95 : 1 }}
                      className={`mainQuizStart__questionNumber ${
                        i === currentQuestion
                          ? "mainQuizStart__questionNumber--active"
                          : ""
                      } ${
                        answers[i] !== undefined
                          ? "mainQuizStart__questionNumber--answered"
                          : ""
                      }`}
                      onClick={() => handleQuestionNavigation(i)}
                      disabled={
                        !allQuestionsAnswered &&
                        answers[currentQuestion] === undefined &&
                        i !== currentQuestion
                      }
                    >
                      {i + 1}
                    </motion.button>
                  )
                )}
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={currentQuestion}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className="mainQuizStart__questionCard"
                >
                  <h3 className="mainQuizStart__question">
                    {quizDetails?.quiz?.questions?.[currentQuestion]?.text}
                  </h3>

                  <div className="mainQuizStart__options">
                    {quizDetails?.quiz?.questions?.[
                      currentQuestion
                    ]?.options?.map((option, index) => (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`mainQuizStart__option ${
                          answers[currentQuestion]?.selectedOptionIndex ===
                          index
                            ? "mainQuizStart__option--selected"
                            : ""
                        }`}
                        onClick={() =>
                          handleSelectOption({
                            questionId:
                              quizDetails?.quiz?.questions?.[currentQuestion]
                                ?._id,
                            selectedOptionIndex: index,
                          })
                        }
                      >
                        <div className="mainQuizStart__optionCircle">
                          {answers[currentQuestion]?.selectedOptionIndex ===
                            index && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="mainQuizStart__optionCircleInner"
                            />
                          )}
                        </div>
                        <span className="mainQuizStart__optionText">
                          {option?.text}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mainQuizStart__footer">
              <motion.button
                whileHover={currentQuestionAnswered ? { scale: 1.05 } : {}}
                whileTap={currentQuestionAnswered ? { scale: 0.95 } : {}}
                className="mainQuizStart__nextButton"
                onClick={
                  allQuestionsAnswered && isLastQuestion
                    ? handleSubmit
                    : handleNext
                }
                disabled={!currentQuestionAnswered}
              >
                {allQuestionsAnswered && isLastQuestion
                  ? "Submit Quiz"
                  : "Next Question"}
              </motion.button>
            </div>
          </motion.div>
        )}

        {quizState === "processing" && (
          <motion.div
            key="processing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="mainQuizStart__processingScreen"
          >
            <motion.div
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mainQuizStart__processingHeader"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="mainQuizStart__processingHeaderIcon"
              >
                🎉
              </motion.div>
              <h2 className="mainQuizStart__processingHeaderTitle">
                Quiz Completed!
              </h2>
            </motion.div>

            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mainQuizStart__carouselContainer"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentMessageIndex}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5 }}
                  className="mainQuizStart__carouselMessage"
                >
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="mainQuizStart__carouselEmoji"
                  >
                    {motivationCarouselMessages[currentMessageIndex].emoji}
                  </motion.div>
                  <p className="mainQuizStart__carouselText">
                    {motivationCarouselMessages[currentMessageIndex].text}
                  </p>
                </motion.div>
              </AnimatePresence>

              <div className="mainQuizStart__carouselIndicators">
                {motivationCarouselMessages.map((_, index) => (
                  <div
                    key={index}
                    className={`mainQuizStart__carouselIndicator ${
                      index === currentMessageIndex
                        ? "mainQuizStart__carouselIndicator--active"
                        : ""
                    }`}
                  />
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mainQuizStart__progressSection"
            >
              <AnimatePresence mode="wait">
                <motion.p
                  key={currentLoadingIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="mainQuizStart__loadingMessage"
                >
                  {loadingMessages[currentLoadingIndex]?.text}
                </motion.p>
              </AnimatePresence>

              <div className="mainQuizStart__progressBar">
                <motion.div
                  className="mainQuizStart__progressBarFill"
                  initial={{ width: "0%" }}
                  animate={{ width: `${processingProgress}%` }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    className="mainQuizStart__progressBarShine"
                    animate={{ x: ["-100%", "200%"] }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                </motion.div>
              </div>

              <motion.p
                className="mainQuizStart__progressPercentage"
                key={processingProgress}
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
              >
                {processingProgress}%
              </motion.p>
            </motion.div>
          </motion.div>
        )}

        {quizState === "timeout" && (
          <motion.div
            key="timeout"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mainQuizStart__completedScreen"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="mainQuizStart__completedIcon"
            >
              ⏰
            </motion.div>
            <h2 className="mainQuizStart__completedTitle">Time Up!</h2>
            <p className="mainQuizStart__completedMessage">
              Your quiz has been automatically submitted.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MainQuizStartComponent;
